import { useMutation } from "@tanstack/react-query";
import { Button, DescriptionList, ErrorAlert } from "@unizpay/ui";
import { AssetLabel } from "../../../components/AssetLabel";
import { api } from "../../../lib/api";
import { useWallet } from "../../../lib/queries";
import type { Profile } from "../../../lib/types";
import { BackButton, StepCard } from "../StepChrome";
import { type Draft, toSettlement } from "../draft";

export function ReviewStep({
  draft,
  goTo,
  onCreated,
}: {
  draft: Draft;
  goTo: (step: number) => void;
  onCreated: (profile: Profile) => void;
}) {
  const wallet = useWallet();
  const p = draft.payout;
  const settlement = toSettlement(p);
  const create = useMutation({
    mutationFn: () =>
      api.account.createProfile({
        name: draft.name.trim(),
        website: null,
        support_email: null,
        settlements: settlement ? [settlement] : [],
      }),
    onSuccess: onCreated,
  });
  const edit = (step: number) => (
    <button
      type="button"
      onClick={() => goTo(step)}
      className="text-[0.8125rem] font-medium text-accent hover:underline"
    >
      Edit
    </button>
  );
  const address = (a: string) => <span className="font-mono text-[0.75rem] break-all">{a}</span>;
  const payoutAddress = p.method === "own" ? p.address.trim() : (wallet.data?.address ?? null);

  return (
    <StepCard
      title="Review and create"
      description="Check the details. Nothing is created until you click Create profile."
      footer={
        <>
          <BackButton onClick={() => goTo(1)} />
          <Button onClick={() => create.mutate()} loading={create.isPending}>
            Create profile
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Business</h2>
            {edit(0)}
          </div>
          <DescriptionList items={[{ label: "Name", value: draft.name.trim() }]} />
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Payout</h2>
            {edit(1)}
          </div>
          {settlement ? (
            <DescriptionList
              items={[
                {
                  label: "Wallet",
                  value: p.method === "own" ? "My own wallet" : "My UnizPay wallet",
                },
                {
                  label: "Receive",
                  value: <AssetLabel token={p.token} network={p.network} networkName={p.networkName} />,
                },
                { label: "Address", value: payoutAddress ? address(payoutAddress) : "Your UnizPay wallet" },
                { label: "Fallback", value: "Your UnizPay wallet" },
              ]}
            />
          ) : (
            <p className="text-[0.8125rem] text-fg-muted">Set later in Settings › Payouts.</p>
          )}
        </div>
        <ErrorAlert error={create.error} title="Could not create the profile" />
      </div>
    </StepCard>
  );
}
